/**
 * How an entered value reads back to a person. Values are stored in the shape
 * generation needs — a picked record as JSON so `{{Client.city}}` resolves, an
 * upload as a data URI — neither of which is fit to show.
 *
 * Pure and i18n-free: the caller translates the non-text outcomes.
 */

import { parsePickedRecord } from './referenceDefaults';

/** Long enough for a sentence, short enough that MultilineText cannot flood the card. */
const MAX_LENGTH = 200;

export type DisplayValue =
  | { kind: 'empty' }
  | { kind: 'file'; image?: string }
  | { kind: 'yesNo'; yes: boolean }
  | { kind: 'text'; text: string; detail?: string };

/** The name of a picked person, company or project; empty when the value is not one. */
export function pickedRecordName(value: string): string {
  return parsePickedRecord(value)?.name ?? '';
}

export function displayValue(propertyType: string, value: string): DisplayValue {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return { kind: 'empty' };

  if (trimmed.startsWith('data:')) {
    return /^data:image\//i.test(trimmed) ? { kind: 'file', image: trimmed } : { kind: 'file' };
  }

  // Not gated on the property type: a stored value outlives a type change, and a
  // default expression can copy a whole record into a plain text property.
  const record = parsePickedRecord(trimmed);
  if (record?.name) {
    return { kind: 'text', text: truncate(record.name), detail: recordDetail(record) || undefined };
  }

  if (propertyType === 'YesNo' && /^(yes|no)$/i.test(trimmed)) {
    return { kind: 'yesNo', yes: trimmed.toLowerCase() === 'yes' };
  }

  return { kind: 'text', text: truncate(trimmed) };
}

/** What tells two same-named records apart, in the order it reads best. */
function recordDetail(fields: Record<string, string>): string {
  const parts = [fields.jobtitle, fields.projectnumber, fields.city, fields.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : fields.email || fields.website || '';
}

function truncate(text: string): string {
  return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH).trimEnd()}…` : text;
}
